import { PRODUCTION_COLUMNS, PROGRAMMING_SCOPE, monthLabel, rowDeliverables, taskColumn, taskScheduleLabel } from "./productionTable";

export const COLUMN_WIDTHS_STORAGE_KEY = "netzer:production-table:widths:v1";
export const TABLE_COLUMN_META = [
  { key: "cliente", label: "Cliente", width: 240, minWidth: 180, maxWidth: 420, kind: "text" },
  { key: "entregaveis", label: "Entregáveis por mês", width: 210, minWidth: 190, maxWidth: 480, kind: "text" },
  { key: "frequencia", label: "Frequência de captação", width: 200, minWidth: 190, maxWidth: 320, kind: "text" },
  ...PRODUCTION_COLUMNS.map(({ key, label }) => ({ key, label, width: 200, minWidth: 190, maxWidth: 520, kind: "tasks" })),
];
const META_BY_KEY = new Map(TABLE_COLUMN_META.map((column) => [column.key, column]));
const FILTER_STATUSES = new Set(["pending", "completed", "overdue", "unscheduled", "proposed", "empty"]);
const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR").replace(/\s+/g, " ").trim();
const isObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

export function clampColumnWidth(key, value) {
  const column = META_BY_KEY.get(key);
  if (!column) return undefined;
  const numeric = typeof value === "number" && Number.isFinite(value) ? value : column.width;
  return Math.min(column.maxWidth, Math.max(column.minWidth, Math.round(numeric)));
}

function normalizedWidths(value) {
  const source = isObject(value) ? value : {};
  return Object.fromEntries(TABLE_COLUMN_META.map((column) => [column.key, clampColumnWidth(column.key, source[column.key])]));
}

export function readColumnWidths(storage) {
  try {
    const store = storage === undefined ? globalThis.localStorage : storage;
    return normalizedWidths(JSON.parse(store?.getItem(COLUMN_WIDTHS_STORAGE_KEY) || "null"));
  } catch {
    return normalizedWidths();
  }
}

// Returning the normalized widths keeps state and persisted values identical,
// including when storage is disabled or full.
export function saveColumnWidths(storage, widths) {
  const next = normalizedWidths(widths);
  try {
    const store = storage === undefined ? globalThis.localStorage : storage;
    store?.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(next));
  } catch { /* The table remains usable without browser persistence. */ }
  return next;
}

function validDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? value : "";
}

function localToday() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function normalizeFilter(filter) {
  const value = isObject(filter) ? filter : {};
  return {
    query: normalize(value.query),
    status: FILTER_STATUSES.has(value.status) ? value.status : "any",
    from: validDate(value.from),
    to: validDate(value.to),
  };
}

export function columnHasFilter(filter) {
  const value = normalizeFilter(filter);
  return !!(value.query || value.status !== "any" || value.from || value.to);
}

export function getColumnFilterCount(filters) {
  return TABLE_COLUMN_META.filter(({ key }) => columnHasFilter(filters?.[key])).length;
}

function textForColumn(row, key) {
  if (key === "cliente") return [row.client?.empresa, ...(row.cycles || []).flatMap((cycle) => [cycle.titulo, monthLabel(cycle.competencia)])].filter(Boolean).join(" ");
  if (key === "entregaveis") return rowDeliverables(row);
  return row.kind === "project" ? "Pontual" : row.client?.frequencia_captacao || "Não informada";
}

function statusMatches(task, status, today) {
  const completed = task.status === "concluido";
  const pending = !completed && task.status !== "cancelado";
  const date = validDate(task.prazo);
  switch (status) {
    case "pending": return pending;
    case "completed": return completed;
    case "overdue": return pending && !!date && date < today;
    case "unscheduled": return !date;
    case "proposed": return task.planejamento_condicao === "proposta";
    default: return true;
  }
}

function taskMatches(task, filter, today) {
  const date = validDate(task.prazo);
  const searchable = [task.titulo, task.responsavel, taskScheduleLabel(task), date, date ? date.split("-").reverse().join("/") : ""].filter(Boolean).join(" ");
  return (!filter.query || normalize(searchable).includes(filter.query))
    && statusMatches(task, filter.status, today)
    && (!filter.from || (!!date && date >= filter.from))
    && (!filter.to || (!!date && date <= filter.to));
}

function rowMatchesColumn(row, column, filter, today) {
  if (column.kind === "text") return !filter.query || normalize(textForColumn(row, column.key)).includes(filter.query);
  const tasks = (row.tasks || []).filter((task) => task.status !== "cancelado" && !task.cliente_encerrado_em && taskColumn(task) === column.key);
  const scopes = column.key === "programacao" ? (row.cycles || []).map((cycle) => PROGRAMMING_SCOPE.find((scope) => scope.key && scope.key === cycle.planejamento_programacao)?.label).filter(Boolean) : [];
  if (filter.status === "empty") return tasks.length === 0 && scopes.length === 0 && !filter.query && !filter.from && !filter.to;
  // A scope annotation is not an operational task, date or completion status.
  if (filter.status === "any" && !filter.from && !filter.to && scopes.some((scope) => normalize(scope).includes(filter.query))) return true;
  // Query, status and date bounds must match the same task, not different
  // entries in a multi-item cell. Keep all tasks visible in matching rows.
  return tasks.some((task) => taskMatches(task, filter, today));
}

export function filterProductionRows(rows, filters = {}, today = localToday()) {
  const date = validDate(today) || localToday();
  const active = TABLE_COLUMN_META.map((column) => ({ column, filter: normalizeFilter(filters?.[column.key]) }))
    .filter(({ column, filter }) => column.kind === "text" ? !!filter.query : columnHasFilter(filter));
  const matches = (row) => active.every(({ column, filter }) => rowMatchesColumn(row, column, filter, date));
  return {
    ...rows,
    recurring: (rows?.recurring || []).filter(matches),
    projects: (rows?.projects || []).filter(matches),
  };
}
